import { z } from 'zod';
import type { Orama, SorterParams, TypedDocument } from '@orama/orama'
import * as FlatModels from '../../internal';

type SearchThumbnailMedia = {
  thumbnail: string;
  type: string;
  editedAt: Date;
  access: string;
};

/* Node Search */
export const NodeOramaSchema = {
  id: "string",
  title: "string",
  content: "string",
  path: "string",
  type: "enum",
  hidden: "boolean",
  restricted: "boolean",
  createdById: "string",
  ancestorNodes: "string[]",
} as const;

export type NodeData = Pick<TypedDocument<Orama<typeof NodeOramaSchema>>, 'id' | 'title' | 'type' | 'path' | 'hidden' | 'restricted' | 'createdById'> & {
  media?: SearchThumbnailMedia;
  pageId: string;
  mediaType?: string;
  content?: string;
  ancestorNodes?: string[];
};

export const NodeSearchSchema = z.object({
  searchString: z.string().default('').optional(),
  typeFilter: z.array(FlatModels.PageEntityModels.PageType).nullable().optional(),
  createdByMe: z.boolean().optional(),
  ids: z.array(z.string().uuid()).optional(),
  paths: z.array(z.string()).optional(),
  usedInFilter: z.array(z.string().uuid()).optional().nullable(),
  sortField: z.string().default('').optional(),
  sortOrder: z.number().optional(),
  first: z.number().default(0),
  rows: z.number().default(10),
  exact: z.boolean().default(false).optional(),
})
export type NodeSearch = z.infer<typeof NodeSearchSchema>;


/* Media Search */
export const MediaOramaSchema = {
  id: "string",
  name: "string",
  type: "enum",
  content: "string",
  hidden: "boolean",
  uploadedBy: "string",
  access: "enum",
  checksum: "string",
  size: "number",
  mime: "enum",
  inNodesWithInheritance: "string[]",
  isUnused: "boolean",
  editedAt: "number",
  createdAt: "number"
} as const;

export type MediaData = Pick<TypedDocument<Orama<typeof MediaOramaSchema>>, 'id' | 'name' | 'type' | 'hidden' | 'uploadedBy' | 'access' | 'checksum' | 'size' | 'mime' | 'editedAt' | 'createdAt'> & {
  originalFile: string;
  file: string;
  thumbnail: string;
  content?: string;
  inNodes?: string[];
  isUnused?: boolean;
  inNodesWithInheritance?: string[];
};

const MediaSearchSortFields = ['editedAt', 'name', 'size'] as const satisfies SorterParams<Orama<typeof MediaOramaSchema>>['property'][];

export const MediaSearchSchema = z.object({
  searchString: z.string().default('').optional(),
  typeFilter: z.array(FlatModels.MediaEntityModels.MediaType).nullable().optional(),
  uploadedByMe: z.boolean().optional(),
  ids: z.array(z.string().uuid()).optional(),
  usedInFilter: z.array(z.string().uuid()).optional().nullable(),
  sortField: z.enum(MediaSearchSortFields).optional().nullable(),
  isDuplicate: z.boolean().optional(),
  selectedIds: z.array(z.string()).optional().nullable(),
  isUnused: z.boolean().optional(),
  sortOrder: z.number(),
  first: z.number().default(0),
  rows: z.number().default(10),
  exact: z.boolean().default(false).optional(),
})
export type MediaSearch = z.infer<typeof MediaSearchSchema>;


/* Detached Page Search */
export type DetachedPageData = 
  Pick<FlatModels.PageEntityModels.Page, 'id' | 'title' | 'type'>  & 
  Pick<FlatModels.PageEntityModels.Slide, 'slideId' | 'status' | 'mediaType'> & {
  media?: SearchThumbnailMedia,
  modality?: {
    id: string;
    name: string;
  }
};

export const DetachedPageSearchSchema = z.object({
  searchString: z.string().default('').optional().nullable(),
  typeFilter: z.array(FlatModels.PageEntityModels.PageType).nullable().optional(),
  createdByMe: z.boolean().optional(),
  sortField: z.string().default('').optional(),
  sortOrder: z.number(),
  first: z.number().default(0),
  rows: z.number().default(10),
  exact: z.boolean().default(false).optional(),
})
export type DetachedPageSearch = z.infer<typeof DetachedPageSearchSchema>;

